"use client";

import type { ReactNode } from "react";
import { TrackingBoundary } from "../../../lib/sentry/index";
import { useRelayRecoveryCount } from "../providers/relay-recovery-provider";
import { ShellTrigger } from "./shell/ResponsiveShell";
import "../styles/component.css";

export interface RelayRecoveryTriggerProps {
  /** Render the child as the trigger instead of wrapping it. Defaults to
   * `true` whenever a child is supplied, matching `RelayHistoryTrigger`'s
   * (and `RelayDepositTrigger`'s) `asChild ?? children != null` rule. */
  asChild?: boolean;
  children?: ReactNode;
}

/**
 * Thin pass-through to `ShellTrigger` (radix `Dialog.Trigger` on desktop /
 * vaul `Drawer.Trigger` on mobile), mirroring `RelayHistoryTrigger` so hosts
 * write `<RelayRecovery.Trigger><button>…</button></RelayRecovery.Trigger>`
 * without knowing which primitive is active. Falls back to a plain-text
 * "Recover" button when no child is given -- unstyled like the sibling
 * `RelayDepositTrigger`/`RelayHistoryTrigger` defaults, so it inherits the
 * host's own button chrome instead of shipping a look of its own.
 *
 * The default button renders NO count badge (2026-07-30: removed on request --
 * the number read as an unread-notification alarm). The root's cross-target
 * recoverable count still reaches the trigger element as
 * `data-rd-recovery-count` (radix/vaul merge Trigger props onto the `asChild`
 * child too), so a host that WANTS a badge can opt in with its own CSS -- e.g.
 * `button[data-rd-recovery-count]:not([data-rd-recovery-count="0"])::after` --
 * without refetching anything.
 *
 * Wrapped in `<TrackingBoundary feature="relay">` for the same reason
 * `RelayHistoryTrigger` is: `data-track="open"` is only read by the delegate a
 * boundary installs, and this trigger sits OUTSIDE the portaled `<Content>`'s
 * own boundary.
 */
export function RelayRecoveryTrigger({ asChild, children }: RelayRecoveryTriggerProps) {
  const recoverableCount = useRelayRecoveryCount();

  return (
    <TrackingBoundary feature="relay">
      <ShellTrigger
        asChild={asChild ?? children != null}
        dataAttributes={{ "data-rd-recovery-count": recoverableCount }}
      >
        {children ?? (
          <button type="button" data-track="open">
            Recover
          </button>
        )}
      </ShellTrigger>
    </TrackingBoundary>
  );
}
